// ===== Imports =====
// NOTE: keep the relative order of style imports — it affects the CSS cascade.
import { createPopper } from "@popperjs/core";
import "bootstrap/dist/js/bootstrap.js";
import "bootstrap/dist/css/bootstrap.min.css";
import "@visual-filter/vue2/dist/styles.css";
import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import { initTheme } from "./plugins/theme";
import i18n from "./plugins/i18n";
import Tabulator from "./plugins/tabulator";
import "@/mixins/locale";
import "@/mixins/route";
import "@/mixins/string";
import _ from "lodash";
import moment from "moment";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { version } from "./../package.json";
import {
  getAllEventConfigs,
  loadFromApi,
} from "@/services/hrdc_event/eventRegistry";
import { createStoreModule } from "@/services/hrdc_event/store/storeFactory";
import { createRoutes as createEventRoutes } from "@/services/hrdc_event/routes/routeFactory";
import { generateModelsForConfig } from "@/services/hrdc_event/models/modelFactory";
import { registerModels } from "@/models/index";
import axios from "axios";
import hrdcFunction from "@/objects/hrdcFunction";
import eventRouteHelper from "@/services/hrdc_event/mixins/eventRouteHelper";
import VueMeta from "vue-meta";
import VueVisualFilter from "@visual-filter/vue2";
import { TiptapVuetifyPlugin } from "tiptap-vuetify";
import "tiptap-vuetify/dist/main.css";

library.add(fas);

// ===== Global properties (Vue.prototype) =====
const service = {
  hrdc: process.env.VUE_APP_SERVICE_URL_HRDC,
  // Legacy alias. The CRM API was folded into the HRDC API, but the key has to
  // stay: objects/service.js defaults every BREAD component to "crm", the three
  // src/services/crm/models/* declare serviceKey: "crm", and /service/crm/... is
  // a live route. Dropping it would silently break all of those.
  crm: process.env.VUE_APP_SERVICE_URL_HRDC,
  log: process.env.VUE_APP_SERVICE_URL_LOG,
  // Legacy alias. The SSO API was folded into the HRDC API. Kept because the 10
  // src/services/sso/models/* declare serviceKey: "sso", the 8 auth endpoints
  // (login, /me, password flows, avatar) + /service/sso/... routes read
  // $service.sso, and permission strings are sso-*. Points at the HRDC URL.
  sso: process.env.VUE_APP_SERVICE_URL_HRDC,
  // Event module service URLs registered dynamically in bootstrap()
};

Vue.prototype.$version = version;
Vue.prototype.$app = { name: process.env.VUE_APP_NAME };
Vue.prototype.$service = service;
Vue.prototype.$env = process.env;
Vue.prototype.$axios = axios;
Vue.prototype.$_ = _;
Vue.prototype.$moment = moment;

// ===== Global components =====
Vue.component(
  "ADashboard",
  require("./components/common/ADashboard.vue").default,
);
Vue.component("AError", require("./components/common/AError.vue").default);
// The single warning/danger banner for every page. Colours resolve from the
// --app-*-bg/-fg/-border theme tokens, so restyling every banner in the app is
// one edit here (or in theme-dark.css), not a sweep across ~30 Add.vue files.
Vue.component(
  "AAlertBanner",
  require("./components/common/AAlertBanner.vue").default,
);
// Site-wide footer. Rendered by the console/developer layouts for every page
// they host; the guest pages render it themselves because each owns a
// full-viewport layout of its own.
Vue.component(
  "AppFooter",
  require("./components/layouts/AppFooter.vue").default,
);
Vue.component("ALoader", require("./components/common/ALoader.vue").default);
Vue.component(
  "BreadHeader",
  require("./components/common/BreadHeader.vue").default,
);
Vue.component("font-awesome-icon", FontAwesomeIcon);
Vue.component(
  "BreadTabulator",
  require("./components/common/BreadTabulator.vue").default,
);
// v-data-table replacement for BreadTabulator. Both stay registered during the
// migration; pages move over one at a time by renaming the tag.
require("./plugins/table-tokens.css");
// Last stylesheet in, so its `.dark-mode …` rules sit after every vendor sheet
// (bootstrap, tabulator, tiptap, visual-filter) in the cascade.
require("./plugins/theme-dark.css");
// The shared surface layer — the floating/frosted material for cards, overlays
// and chrome. Reads the tokens theme-dark.css defines, so it must come after
// it. Every selector in it is #app-prefixed and none of them use !important;
// see the specificity contract at the top of the file for why.
require("./plugins/theme-glass.css");
Vue.component(
  "BreadDataTable",
  require("./components/common/BreadDataTable.vue").default,
);
Vue.component(
  "BreadTabulatorFilterable",
  require("./components/common/BreadTabulatorFilterable.vue").default,
);
Vue.component(
  "BreadTabulatorSearchable",
  require("./components/common/BreadTabulatorSearchable.vue").default,
);
Vue.component(
  "BreadTabulatorAdvanceFilterable",
  require("./components/common/BreadTabulatorAdvanceFilterable.vue").default,
);
Vue.component(
  "BreadTabulatorSelectColumn",
  require("./components/common/BreadTabulatorSelectColumn.vue").default,
);
Vue.component(
  "BreadTabulatorForm",
  require("./components/common/BreadTabulatorForm.vue").default,
);
// Read-page pieces. These replaced BreadTabulatorDataView and the
// BreadTabulatorTabChild / …ChildV2 / …TabIntermediate trio, which the read
// page was the only consumer of.
Vue.component(
  "BreadDataRecordView",
  require("./components/common/BreadDataRecordView.vue").default,
);
Vue.component(
  "BreadDataRecordList",
  require("./components/common/BreadDataRecordList.vue").default,
);
Vue.component(
  "BreadDataTableTabChild",
  require("./components/common/BreadDataTableTabChild.vue").default,
);
Vue.component(
  "BreadModelForm",
  require("./components/common/BreadModelForm.vue").default,
);
Vue.component(
  "BreadTabulatorViewLatest",
  require("./components/common/BreadTabulatorViewLatest.vue").default,
);
Vue.component(
  "ATabulatorLoder",
  require("./components/common/ATabulatorLoder.vue").default,
);
Vue.component(
  "BreadTabulatorExport",
  require("./components/common/BreadTabulatorExport.vue").default,
);
Vue.component(
  "BreadTabulatorExportAll",
  require("./components/common/BreadTabulatorExportAll.vue").default,
);
Vue.component(
  "HrdcLoader",
  require("./services/hrdc/components/HrdcLoader.vue").default,
);

// ===== Plugins =====
Vue.use(VueMeta);
Vue.use(VueVisualFilter);
Vue.use(TiptapVuetifyPlugin, {
  vuetify,
  iconsGroup: "mdi",
});

// ===== Mixins & config =====
Vue.config.productionTip = false;
// Binds the stored light/dark preference to Vuetify and to the <html> class
// before anything renders. Must run before mount, not in a layout's created().
initTheme(vuetify);
Vue.mixin(hrdcFunction);
Vue.mixin(eventRouteHelper);

// Async bootstrap: load ALL event modules from API, then mount
async function bootstrap() {
  // Load event modules from API (registry starts empty)
  await loadFromApi();

  // Register ALL event modules dynamically
  const allConfigs = getAllEventConfigs();
  const LayoutConsoleV2 = () =>
    import("@/components/layouts/LayoutConsoleV2.vue");

  for (const config of allConfigs) {
    // Register service URL
    service[config.serviceKey] = config.serviceUrl;

    // Register Vuex store module with persisted state from localStorage
    const storeModule = createStoreModule(config);
    try {
      const saved = localStorage.getItem(`eventStore_${config.serviceKey}`);
      if (saved) {
        storeModule.state.data = JSON.parse(saved);
      }
    } catch (e) {
      /* ignore */
    }
    store.registerModule(config.storeModuleName, storeModule);

    // Register routes under LayoutConsoleV2
    router.addRoutes([
      {
        path: "/",
        component: LayoutConsoleV2,
        children: [createEventRoutes(config)],
      },
    ]);

    // Register models
    registerModels(generateModelsForConfig(config));
  }

  // Shared Event Modules settings route (not per-module — the page applies to all events)
  if (allConfigs.length > 0) {
    router.addRoutes([
      {
        path: "/",
        component: LayoutConsoleV2,
        children: [
          {
            path: "/service/:serviceKey",
            component: () => import("@/components/layouts/LayoutNested.vue"),
            children: [
              {
                path: "settings/event_modules",
                name: "HrdcEventSettingsEventModules",
                component: () =>
                  import(
                    "@/services/hrdc_event/views/settings/event_modules/Index.vue"
                  ),
              },
              {
                path: "settings/email_reminder",
                name: "HrdcEventSettingsEmailReminder",
                component: () =>
                  import(
                    "@/services/hrdc_event/views/settings/email_reminder/Index.vue"
                  ),
              },
            ],
          },
        ],
      },
    ]);
  }

  // Persist event module store changes to localStorage
  const eventMutationNames = new Set(
    allConfigs.map((c) => c.storeMutationName),
  );
  const eventModuleKeyMap = {};
  allConfigs.forEach((c) => {
    eventModuleKeyMap[c.storeMutationName] = c.serviceKey;
  });
  store.subscribe((mutation) => {
    if (eventMutationNames.has(mutation.type)) {
      const serviceKey = eventModuleKeyMap[mutation.type];
      try {
        localStorage.setItem(
          `eventStore_${serviceKey}`,
          JSON.stringify(mutation.payload),
        );
      } catch (e) {
        /* ignore */
      }
    }
  });

  // Add 404 catch-all LAST so dynamic routes are matched first
  router.addRoutes([
    {
      path: "/:catchAll(.*)",
      name: "PageErrorNotFound",
      component: () => import("@/views/error/PageErrorNotFound.vue"),
    },
  ]);

  new Vue({
    router,
    store,
    vuetify,
    i18n,
    Tabulator,
    createPopper,
    library,
    FontAwesomeIcon,
    render: (h) => h(App),
  }).$mount("#app");
}

bootstrap();
